"use client";
import { useState, useEffect } from "react";
import { useRouter } from "next/navigation";
import { 
  Rocket, Wifi, WifiOff, LayoutDashboard, Smartphone, CalendarClock, Bot, 
  Settings, Users, PieChart, Zap, Shield, LogOut, Flame, Store
} from "lucide-react";

import QrCodeManager from "@/components/QrCodeManager"; 
import Operacional from "@/components/views/Operacional";
import Estrategico from "@/components/views/Estrategico";

export default function VipBroadcaster() {
  const router = useRouter(); 
  const [isAuthorized, setIsAuthorized] = useState(false); 
  const [currentUser, setCurrentUser] = useState<any>(null); 
  const [activeTab, setActiveTab] = useState("dashboard");
  const [isBackendOnline, setIsBackendOnline] = useState<boolean>(true); 

  useEffect(() => {
    const token = localStorage.getItem("vip_token");
    const userStr = localStorage.getItem("vip_user"); 
    if (!token) {
      router.push("/login");
    } else {
      setIsAuthorized(true); 
      if (userStr) setCurrentUser(JSON.parse(userStr));
    }
  }, [router]);

  const handleLogout = () => {
    localStorage.removeItem("vip_token");
    localStorage.removeItem("vip_user");
    router.push("/login");
  };

  if (!isAuthorized) {
    return <div className="h-screen w-screen bg-slate-900 flex items-center justify-center text-white font-bold text-xl">Verificando credenciais...</div>;
  }

  return (
    <div className="flex h-screen bg-slate-50 font-sans text-slate-800 overflow-hidden">
      
      {/* MENU LATERAL PRETO (O CHASSI) */}
      <aside className="w-72 bg-slate-900 text-white flex flex-col shadow-2xl z-10">
        <div className="p-8 flex items-center gap-4 border-b border-slate-800">
          <div className="w-12 h-12 bg-blue-600 rounded-xl flex items-center justify-center shadow-lg shadow-blue-600/20">
            <Rocket className="text-white w-6 h-6" />
          </div>
          <div>
            <h1 className="text-xl font-black tracking-tight">NewIt VIP</h1>
            <p className="text-xs font-medium text-slate-400">v2.0 PRO</p>
          </div>
        </div>
        
        <nav className="flex-1 px-4 py-6 space-y-2 overflow-y-auto">
          <MenuButton icone={<LayoutDashboard />} label="Área de Trabalho" active={activeTab === 'dashboard'} onClick={() => setActiveTab('dashboard')} />
          <MenuButton icone={<CalendarClock />} label="Agendamentos" active={activeTab === 'agendamentos'} onClick={() => setActiveTab('agendamentos')} />
          <MenuButton icone={<PieChart />} label="Relatórios" active={activeTab === 'relatorios'} onClick={() => setActiveTab('relatorios')} />
          <MenuButton icone={<Flame />} label="Leads / CRM" active={activeTab === 'crm'} onClick={() => setActiveTab('crm')} />
          
          {/* 🔥 NOSSO NOVO BOTÃO DA LOJA AQUI! */}
          <MenuButton icone={<Store />} label="Eventos & Catálogo" active={activeTab === 'catalogo'} onClick={() => setActiveTab('catalogo')} />
          
          <MenuButton icone={<Users />} label="Gestão de Grupos" active={activeTab === 'grupos'} onClick={() => setActiveTab('grupos')} />
          <MenuButton icone={<Bot />} label="Inteligência Artificial" active={activeTab === 'ia'} onClick={() => setActiveTab('ia')} />
          
          <div className="pt-6 pb-2"><p className="px-4 text-xs font-bold text-slate-500 uppercase tracking-wider">Sistema</p></div>
          <MenuButton icone={<Smartphone />} label="Conexões" active={activeTab === 'conexoes'} onClick={() => setActiveTab('conexoes')} />
          <MenuButton icone={<Zap />} label="Automações e Radar" active={activeTab === 'configs'} onClick={() => setActiveTab('configs')} />
          
          {currentUser?.role === 'ADMIN' && (
            <>
              <div className="pt-6 pb-2"><p className="px-4 text-xs font-bold text-emerald-500/70 uppercase tracking-wider">Administração</p></div>
              <MenuButton icone={<Shield />} label="Gerenciador de Usuários" active={activeTab === 'usuarios'} onClick={() => setActiveTab('usuarios')} />
            </>
          )}
        </nav>

        <div className="p-6 border-t border-slate-800 space-y-3">
          <div className={`flex items-center gap-3 px-4 py-3 rounded-xl border ${isBackendOnline ? 'bg-emerald-500/10 border-emerald-500/20 text-emerald-400' : 'bg-red-500/10 border-red-500/20 text-red-400'}`}>
            {isBackendOnline ? <Wifi className="w-5 h-5" /> : <WifiOff className="w-5 h-5" />}
            <span className="text-sm font-bold truncate flex-1">{currentUser?.name || 'Motor Online'}</span>
          </div>
          <button onClick={handleLogout} className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-red-500/10 hover:bg-red-500/20 text-red-400 rounded-xl font-bold transition-all">
            <LogOut className="w-4 h-4" /> Sair do Painel
          </button>
        </div>
      </aside>

      {/* ÁREA PRINCIPAL */}
      <main className="flex-1 overflow-y-auto p-10">
        {activeTab !== 'relatorios' && activeTab !== 'usuarios' && activeTab !== 'crm' && (
          <header className="mb-10">
            <h1 className="text-3xl font-black text-slate-900 capitalize">
              {activeTab === 'dashboard' ? 'Visão Geral' : activeTab === 'configs' ? 'Automações Inteligentes' : activeTab === 'ia' ? 'Inteligência Artificial' : activeTab.replace('-', ' ')}
            </h1>
            <p className="text-slate-500 mt-1">Gerencie suas campanhas e automações.</p>
          </header>
        )}
        
        {/* PLUGANDO O BLOCO OPERACIONAL (AGORA COM O CATÁLOGO!) */}
        {(activeTab === 'dashboard' || activeTab === 'agendamentos' || activeTab === 'grupos' || activeTab === 'catalogo') && (
          <Operacional activeTab={activeTab} setActiveTab={setActiveTab} />
        )}

        {/* PLUGANDO O BLOCO ESTRATÉGICO */}
        {(activeTab === 'relatorios' || activeTab === 'crm' || activeTab === 'configs' || activeTab === 'ia' || activeTab === 'usuarios') && (
          <Estrategico activeTab={activeTab} setActiveTab={setActiveTab} currentUser={currentUser} />
        )}

        {/* PLUGANDO AS CONEXÕES */}
        {activeTab === 'conexoes' && (
          <div className="max-w-md mx-auto"><QrCodeManager /></div>
        )}
      </main>
    </div>
  );
}

// O COMPONENTE DE BOTÃO DO MENU
function MenuButton({ icone, label, active, onClick }: { icone: React.ReactNode, label: string, active: boolean, onClick: () => void }) {
  return (
    <button onClick={onClick} className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl font-semibold transition-all ${active ? 'bg-blue-600 text-white shadow-lg shadow-blue-600/20' : 'text-slate-400 hover:bg-slate-800 hover:text-slate-200'}`}>
      <div className="w-5 h-5">{icone}</div>{label}
    </button>
  );
}